import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  loadMoreWeeksPast,
  loadMoreWeeksFuture,
  WEEKS_TO_LOAD,
} from '../ducks/plannerActions';
import { getCurrentWeek, getYearFromWeek } from '../utils/weekUtils';
import {
  ROW_HEIGHT,
  TABLE_HEADER_HEIGHT,
  SCROLL_THRESHOLD_PX,
  YEAR_TRANSITION_DELAY,
} from '../utils/gridConstants';

/**
 * Hook to manage scroll behavior for ReviewView.
 * Handles auto-scroll to current week, year tracking, infinite scroll, and visibility events.
 *
 * @param {Object} options
 * @param {Array} options.weeks - Array of week strings
 * @param {Object} options.loadedWeekRange - { fromWeek, toWeek }
 * @param {boolean} options.isLoadingMoreWeeks - Whether more weeks are being loaded
 * @returns {Object} - { timelineRef, currentYear, previousYear, handleJumpToToday }
 */
const useReviewViewScroll = ({
  weeks,
  loadedWeekRange,
  isLoadingMoreWeeks,
}) => {
  const dispatch = useDispatch();
  const timelineRef = useRef(null);
  const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
  const [previousYear, setPreviousYear] = useState(null);
  const isLoadingPastRef = useRef(false);
  const isLoadingFutureRef = useRef(false);
  const scrollAnchorRef = useRef(null);
  const hasAutoScrolledRef = useRef(false);
  const yearTransitionTimeoutRef = useRef(null);
  const currentWeekRowRef = useRef(null);
  const tickingRef = useRef(false);
  // Cache bounding rects to avoid expensive recalculations on every scroll (Safari perf issue)
  const lastRectCacheRef = useRef({ time: 0, containerRect: null, rowRect: null });

  const currentWeekString = getCurrentWeek();

  // Cache the current week row element when weeks or currentWeekString changes
  useEffect(() => {
    const container = timelineRef.current;
    if (container && weeks.length > 0) {
      currentWeekRowRef.current = container.querySelector(
        `[data-week-row="${currentWeekString}"]`,
      );
    }
  }, [weeks.length, currentWeekString]);

  // Auto-scroll to current week on mount (position at top, below sticky header)
  useEffect(() => {
    if (timelineRef.current && weeks.length > 0 && !hasAutoScrolledRef.current) {
      const currentWeekIndex = weeks.indexOf(currentWeekString);
      if (currentWeekIndex !== -1) {
        // Find the current week row element and scroll it into view
        const currentWeekRow = timelineRef.current.querySelector(
          `[data-week-row="${currentWeekString}"]`,
        );
        if (currentWeekRow) {
          // Use scrollIntoView with block: 'start' to position at top
          // The sticky header is accounted for automatically by the browser
          currentWeekRow.scrollIntoView({ block: 'start', behavior: 'instant' });
          hasAutoScrolledRef.current = true;
        }
      }
    }
  }, [weeks.length, currentWeekString, weeks]);

  // Synchronously restore scroll position after weeks are added (prevents jump)
  useLayoutEffect(() => {
    const container = timelineRef.current;
    if (!container) return;

    // If we just loaded past weeks, restore the visual scroll position
    if (scrollAnchorRef.current) {
      const { shouldRestore, targetScrollTop } = scrollAnchorRef.current;
      if (shouldRestore) {
        container.scrollTop = targetScrollTop;
        scrollAnchorRef.current = null; // Clear the anchor
      }
    }
  }, [weeks.length]);

  // Unified scroll handler: Year tracking, visibility events, and infinite scroll
  // Combined into single listener to avoid duplicate event processing
  useEffect(() => {
    const container = timelineRef.current;
    if (!container || weeks.length === 0) return undefined;

    const updateScrollState = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;

      // Find the actual visible week by checking DOM
      const allRows = container.querySelectorAll('[data-week-row]');
      let visibleWeek = null;
      const containerRect = container.getBoundingClientRect();
      const headerBottom = containerRect.top + TABLE_HEADER_HEIGHT;

      // Find first row that's actually visible (below the sticky header)
      for (let i = 0; i < allRows.length; i += 1) {
        const rowRect = allRows[i].getBoundingClientRect();
        if (rowRect.bottom > headerBottom) {
          visibleWeek = allRows[i].getAttribute('data-week-row');
          break;
        }
      }

      if (visibleWeek) {
        const year = getYearFromWeek(visibleWeek);
        setCurrentYear((prevYear) => {
          if (year !== prevYear) {
            setPreviousYear(prevYear);
            // Clear any existing timeout to prevent memory leaks
            if (yearTransitionTimeoutRef.current) {
              clearTimeout(yearTransitionTimeoutRef.current);
            }
            yearTransitionTimeoutRef.current = setTimeout(
              () => setPreviousYear(null),
              YEAR_TRANSITION_DELAY,
            );
          }
          return year;
        });
      }

      // === Current Week Visibility ===
      // Use cached DOM element and throttled bounding rect calculations (Safari perf)
      const currentWeekRow = currentWeekRowRef.current;
      if (currentWeekRow) {
        const now = performance.now();
        const cache = lastRectCacheRef.current;

        // Only recalculate bounding rects every 100ms to avoid layout thrashing on Safari
        if (now - cache.time > 100 || !cache.containerRect) {
          lastRectCacheRef.current = {
            time: now,
            containerRect: container.getBoundingClientRect(),
            rowRect: currentWeekRow.getBoundingClientRect(),
          };
        }

        const { containerRect: cachedContainerRect, rowRect } = lastRectCacheRef.current;
        if (cachedContainerRect && rowRect) {
          const visibleTop = cachedContainerRect.top + TABLE_HEADER_HEIGHT;
          const visibleBottom = cachedContainerRect.bottom;
          const isVisible = rowRect.bottom > visibleTop && rowRect.top < visibleBottom;

          window.dispatchEvent(new CustomEvent('planner:weekVisibilityChange', {
            detail: { isVisible },
          }));
        }
      }

      // === Infinite Scroll ===
      if (loadedWeekRange.fromWeek && loadedWeekRange.toWeek) {
        // Check if scrolling near the top edge (past)
        if (scrollTop <= SCROLL_THRESHOLD_PX && !isLoadingPastRef.current && !isLoadingMoreWeeks) {
          isLoadingPastRef.current = true;
          const currentScrollTop = scrollTop;

          dispatch(loadMoreWeeksPast(loadedWeekRange.fromWeek))
            .then(() => {
              scrollAnchorRef.current = {
                shouldRestore: true,
                targetScrollTop: currentScrollTop + (WEEKS_TO_LOAD * ROW_HEIGHT),
              };
            })
            .finally(() => {
              isLoadingPastRef.current = false;
            });
        }

        // Check if scrolling near the bottom edge (future)
        const scrollBottom = scrollHeight - scrollTop - clientHeight;
        if (
          scrollBottom <= SCROLL_THRESHOLD_PX
          && !isLoadingFutureRef.current
          && !isLoadingMoreWeeks
        ) {
          isLoadingFutureRef.current = true;

          dispatch(loadMoreWeeksFuture(loadedWeekRange.toWeek)).finally(() => {
            isLoadingFutureRef.current = false;
          });
        }
      }
    };

    const handleScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(() => {
        updateScrollState();
        tickingRef.current = false;
      });
    };

    // Initial update
    updateScrollState();

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      // Clean up year transition timeout on unmount
      if (yearTransitionTimeoutRef.current) {
        clearTimeout(yearTransitionTimeoutRef.current);
      }
    };
  }, [
    weeks,
    loadedWeekRange.fromWeek,
    loadedWeekRange.toWeek,
    isLoadingMoreWeeks,
    dispatch,
  ]);

  // Handle "Jump to Today" button click (position current week at top, below sticky header)
  const handleJumpToToday = useCallback(() => {
    // Use cached ref for better performance, fall back to query if needed
    const currentWeekRow = currentWeekRowRef.current
      || timelineRef.current?.querySelector(`[data-week-row="${currentWeekString}"]`);

    if (currentWeekRow) {
      currentWeekRow.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [currentWeekString]);

  // Listen for today button clicks from header
  useEffect(() => {
    const handleTodayEvent = () => {
      handleJumpToToday();
    };

    window.addEventListener('planner:jumpToToday', handleTodayEvent);
    return () => {
      window.removeEventListener('planner:jumpToToday', handleTodayEvent);
    };
  }, [handleJumpToToday]);

  return {
    timelineRef,
    currentYear,
    previousYear,
    handleJumpToToday,
  };
};

export default useReviewViewScroll;
